/** Export the finished document as real text, never as a screenshot. */
import type { Content, TDocumentDefinitions } from "pdfmake/interfaces";

interface ExportOptions {
  elementId: string;
  filenamePrefix: string;
  industryLabel: string;
  companyName?: string;
  accentColor?: string;
  logoUrl?: string;
  footerText?: string;
  brandingTextPosition?: "header" | "footer" | "both";
  removeBranding?: boolean;
}

/** Only actual photos and logos are rasterized; all proposal words stay text. */
async function imageData(url: string): Promise<string> {
  const image = new Image();
  image.crossOrigin = "anonymous";
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("A document image could not be loaded."));
    image.src = url;
  });
  const canvas = document.createElement("canvas");
  const ratio = Math.min(1, 1600 / Math.max(image.naturalWidth, image.naturalHeight));
  canvas.width = Math.max(1, Math.round(image.naturalWidth * ratio));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * ratio));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Document image processing is unavailable.");
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/png");
}

function documentColor(root: HTMLElement, token: string, fallback: string): string {
  const color = getComputedStyle(root).getPropertyValue(token).trim();
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context || !color) return fallback;
  context.fillStyle = fallback;
  context.fillStyle = color;
  return context.fillStyle;
}

export async function exportProposalPDF(opts: ExportOptions): Promise<void> {
  const source = document.getElementById(opts.elementId);
  if (!source) throw new Error("The finished proposal could not be found.");
  const root = source.matches(".proposal-document") ? source : source.querySelector<HTMLElement>(".proposal-document") ?? source;
  const [{ default: pdfMake }, { default: fonts }, { default: htmlToPdfmake }, { marked }] = await Promise.all([
    import("pdfmake/build/pdfmake"), import("pdfmake/build/vfs_fonts"), import("html-to-pdfmake"), import("marked"),
  ]);
  pdfMake.vfs = fonts as unknown as Record<string, string>;

  const paper = root.cloneNode(true) as HTMLElement;
  paper.querySelectorAll("button, svg, input, select, script, style, [data-html2canvas-ignore], .proposal-document-footer").forEach(node => node.remove());
  // Include the current editor value, not the surrounding Save/Cancel controls.
  paper.querySelectorAll("textarea").forEach(node => {
    const paragraph = document.createElement("p");
    paragraph.textContent = node.value;
    node.replaceWith(paragraph);
  });
  const originalLetterhead = root.querySelector(".proposal-letterhead");
  const letterhead = paper.querySelector(".proposal-letterhead");
  const displayName = opts.companyName || originalLetterhead?.querySelector("p")?.textContent?.trim() || (opts.removeBranding ? opts.industryLabel : "Sentinel Forge Systems");
  const title = originalLetterhead?.querySelector("h2")?.textContent?.trim() || opts.industryLabel;
  const contact = originalLetterhead?.querySelectorAll("p")[1]?.textContent?.trim() || "";
  const logoUrl = opts.logoUrl || originalLetterhead?.querySelector("img")?.src;
  const logo = logoUrl ? await imageData(logoUrl) : undefined;
  letterhead?.remove();

  // Plain generated sections can contain Markdown tables, bold text and lists.
  for (const node of paper.querySelectorAll<HTMLElement>(".prose, p, pre")) {
    if (node.children.length === 0 && node.textContent?.trim()) {
      const parsed = marked.parse(node.textContent, { async: false, breaks: true });
      const wrapper = document.createElement("div");
      wrapper.innerHTML = parsed;
      wrapper.querySelectorAll("script, style, iframe, object, embed, img").forEach(child => child.remove());
      node.replaceWith(wrapper);
    }
  }
  // Semantic HTML only: no application colors, fixed sizes, animations or controls.
  for (const node of [paper, ...paper.querySelectorAll<HTMLElement>("*")]) {
    const imageUrl = node instanceof HTMLImageElement ? node.src : undefined;
    const attributes = [...node.attributes];
    for (const attribute of attributes) node.removeAttribute(attribute.name);
    if (imageUrl) node.setAttribute("src", await imageData(imageUrl));
  }
  const ink = documentColor(root, "--document-ink", "#191919");
  const heading = opts.accentColor && /^#[a-f\d]{6}$/i.test(opts.accentColor)
    ? opts.accentColor : documentColor(root, "--document-heading", "#245578");
  const highlight = documentColor(root, "--document-highlight", "#fff5cc");
  const converted = htmlToPdfmake(paper.innerHTML, {
    defaultStyles: {
      div: { margin: [0, 0, 0, 0] },
      h1: { fontSize: 22, bold: true, color: heading, margin: [0, 18, 0, 9] },
      h2: { fontSize: 17, bold: true, color: heading, margin: [0, 16, 0, 8] },
      h3: { fontSize: 13, bold: true, color: heading, margin: [0, 15, 0, 7] },
      h4: { fontSize: 12, bold: true, color: heading, margin: [0, 14, 0, 7] },
      p: { margin: [0, 0, 0, 9] },
      ul: { margin: [0, 0, 0, 10] }, ol: { margin: [0, 0, 0, 10] },
      th: { bold: true, color: ink, fillColor: highlight },
    },
  });
  // Make every table fit the printable width and allow long rows to flow.
  function normalize(value: unknown): void {
    if (Array.isArray(value)) { value.forEach(normalize); return; }
    if (!value || typeof value !== "object") return;
    const node = value as Record<string, unknown>;
    if (node.table && typeof node.table === "object") {
      const table = node.table as { body?: unknown[][]; widths?: string[]; headerRows?: number };
      const count = Math.max(...(table.body ?? []).map(row => row.length), 0);
      table.widths = Array.from({ length: count }, () => "*");
      if (table.body?.[0]?.some(cell => JSON.stringify(cell).includes("html-th"))) table.headerRows = 1;
      node.layout = "lightHorizontalLines";
      node.fontSize = 9;
      node.margin = [0, 6, 0, 12];
    }
    if (Array.isArray(node.style) && node.style.some(style => /^html-h[1-6]$/.test(String(style)))) {
      node.headlineLevel = 1;
    }
    if (node.image) { node.fit = [450, 240]; delete node.width; delete node.height; node.margin = [0, 8, 0, 12]; }
    Object.values(node).forEach(normalize);
  }
  normalize(converted);
  const timestamp = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const position = opts.brandingTextPosition ?? "footer";
  const customText = opts.footerText?.trim() || "";
  const headerText = position !== "footer" ? customText : "";
  const footerText = position !== "header" ? customText : "";
  if (customText.length > 1200) throw new Error("Please shorten the custom header/footer to 1,200 characters.");
  const headerHeight = 66 + (headerText ? Math.ceil(headerText.length / 90) * 10 : 0);
  const footerHeight = 48 + (footerText ? Math.ceil(footerText.length / 90) * 10 : 0);
  const rule: Content = { canvas: [{ type: "line", x1: 0, y1: 0, x2: 504, y2: 0, lineWidth: 0.7, lineColor: heading }], margin: [0, 8, 0, 14] };
  const definition: TDocumentDefinitions = {
    pageSize: "LETTER", pageMargins: [54, headerHeight, 54, footerHeight],
    info: { title: `${displayName} — ${title}`, author: displayName, subject: title },
    defaultStyle: { font: "Roboto", fontSize: 10.5, lineHeight: 1.3, color: ink },
    header: () => ({ margin: [54, 24, 54, 0], stack: [
      { columns: [ ...(logo ? [{ image: logo, fit: [40, 24] as [number, number], width: 52 }] : []),
        { text: displayName, bold: true, fontSize: 10, color: heading },
        { text: title, alignment: "right" as const, fontSize: 8, width: 170 } ] },
      ...(headerText ? [{ text: headerText, fontSize: 8, margin: [0, 5, 0, 0] as [number, number, number, number] }] : []),
    ] }),
    footer: (page, total) => ({ margin: [54, 8, 54, 0], stack: [
      ...(footerText ? [{ text: footerText, fontSize: 8, margin: [0, 0, 0, 6] as [number, number, number, number] }] : []),
      { columns: [ { text: `${opts.removeBranding ? "Prepared" : "Prepared with Sentinel Forge Systems"} · ${timestamp}`, fontSize: 7 },
        { text: `Page ${page} of ${total}`, alignment: "right" as const, fontSize: 7, width: 70 } ] },
    ] }),
    content: [
      { columns: [ ...(logo ? [{ image: logo, fit: [68, 48] as [number, number], width: 84 }] : []),
        { stack: [{ text: displayName, bold: true, fontSize: 13, margin: [0, 0, 0, 5] },
          { text: title, fontSize: 24, bold: true, color: heading },
          ...(contact ? [{ text: contact, fontSize: 9, margin: [0, 6, 0, 0] as [number, number, number, number] }] : [])] } ] },
      rule, converted,
    ],
    pageBreakBefore: (node, following) => Boolean(node.headlineLevel &&
      (following.length === 0 || node.startPosition.verticalRatio > 0.8)),
  };
  // Use a Blob download so the export completes only after generation succeeds.
  const blob = await new Promise<Blob>(resolve => pdfMake.createPdf(definition).getBlob(resolve));
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${opts.filenamePrefix}-${Date.now()}.pdf`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
